import { useQuery } from "@tanstack/react-query";
import type { GetOrganizationsResponseDto } from "@workspace/dto/organization/get-organizations";
import { Skeleton } from "@workspace/ui/components/skeleton";
import type { AxiosResponse } from "axios";
import type { ExtendedUser } from "@/auth";
import { OrganizationDropdown } from "@/components/organization-dropdown";
import { useStableSession } from "@/lib/auth/use-stable-session";
import { axios } from "@/lib/axios";
import { queryKeys } from "@/lib/query-keys";

export function OrganizationMenu({
  isExpanded,
  onOpenChange,
}: {
  isExpanded: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const { data: session } = useStableSession();
  const user = session?.user as ExtendedUser | undefined;
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.account.organizations,
    queryFn: async () => {
      const response = await axios.get<
        GetOrganizationsResponseDto,
        AxiosResponse<GetOrganizationsResponseDto>
      >("/account/get-organizations");
      return response.data;
    },
    enabled: !!user && user.type === "employer",
  });

  if (isLoading || !session?.user) {
    return (
      <div
        aria-hidden="true"
        className="flex h-10 items-center"
        data-testid="organization-menu-skeleton"
      >
        <Skeleton className="size-8 shrink-0 rounded-sm" />
        {isExpanded && <Skeleton className="ml-3 h-4 w-28" />}
      </div>
    );
  }

  return (
    data && (
      <OrganizationDropdown
        isExpanded={isExpanded}
        onOpenChange={onOpenChange}
        organizations={data.organizations}
        selectedOrganizationId={(session.user as ExtendedUser).organization}
      />
    )
  );
}
